import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Loader2, Send, Bot, Wrench, CheckCircle2, XCircle, ExternalLink, Trash2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { ChatBubble, TypingIndicator } from "@/components/chat/ChatBubble";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

type ToolStatus = "awaiting_confirmation" | "executed" | "failed" | "cancelled";
type Msg = {
  id?: string;
  role: "user" | "assistant" | "tool";
  content: string;
  tool_name?: string | null;
  tool_result?: any;
};

const WRITE_TOOL_LABELS: Record<string, string> = {
  navigate_to_page: "supportChat.tool.navigate",
  update_profile: "supportChat.tool.updateProfile",
  mark_alerts_read: "supportChat.tool.markAlerts",
};

interface Props {
  height?: string;
  initialChatId?: string;
  compact?: boolean;
}

export default function SupportAssistantPanel({ height = "h-[560px]", initialChatId, compact = false }: Props) {
  const { t } = useTranslation();
  const { session } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [chatId, setChatId] = useState<string | undefined>(initialChatId);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [pendingTool, setPendingTool] = useState<{ messageId: string; name: string; args: any } | null>(null);
  const [executing, setExecuting] = useState(false);
  const [streamingIdx, setStreamingIdx] = useState<number | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (initialChatId) {
      supabase.from("support_chat_messages")
        .select("id,role,content,tool_name,tool_result")
        .eq("chat_id", initialChatId)
        .order("created_at", { ascending: true })
        .limit(50)
        .then(({ data }) => { if (data) setMessages(data as Msg[]); });
      return;
    }
    // Load the user's persistent single chat (latest one).
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data: chat } = await supabase.from("support_chats")
        .select("id").eq("user_id", user.id)
        .order("updated_at", { ascending: false }).limit(1).maybeSingle();
      if (!chat?.id) return;
      setChatId(chat.id);
      const { data: msgs } = await supabase.from("support_chat_messages")
        .select("id,role,content,tool_name,tool_result,created_at")
        .eq("chat_id", chat.id)
        .order("created_at", { ascending: false })
        .limit(50);
      if (msgs) setMessages((msgs as any[]).reverse() as Msg[]);
    })();
  }, [initialChatId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading, pendingTool]);

  const callChat = async (payload: any) => {
    const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/support-assistant-chat`;
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.access_token}` },
      body: JSON.stringify({ chat_id: chatId, ...payload }),
    });
    const j = await res.json();
    if (!res.ok) throw new Error(j.error || `HTTP ${res.status}`);
    return j as { chat_id: string; assistant_message_id?: string; content: string; tool_request?: { name: string; args: any } | null; was_free?: boolean; free_remaining?: number };
  };

  const handleResponse = (j: Awaited<ReturnType<typeof callChat>>) => {
    if (!chatId) setChatId(j.chat_id);
    const isNavigate = j.tool_request?.name === "navigate_to_page";
    setMessages(prev => {
      const next = [...prev, {
        id: j.assistant_message_id, role: "assistant" as const, content: j.content,
        tool_name: j.tool_request?.name,
        tool_result: j.tool_request ? { status: "awaiting_confirmation", args: j.tool_request.args } : undefined,
      }];
      setStreamingIdx(next.length - 1);
      return next;
    });
    if (j.tool_request && j.assistant_message_id && !isNavigate) {
      setPendingTool({ messageId: j.assistant_message_id, name: j.tool_request.name, args: j.tool_request.args });
    }
  };

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: text }]);
    setLoading(true);
    try {
      const j = await callChat({ message: text });
      handleResponse(j);
    } catch (e: any) {
      setMessages(prev => [...prev, { role: "assistant", content: `⚠️ ${e.message}` }]);
      toast({ title: t("supportChat.error"), description: e.message, variant: "destructive" });
    } finally { setLoading(false); }
  };

  const confirmTool = async () => {
    if (!pendingTool) return;
    setExecuting(true);
    try {
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/support-action-execute`;
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.access_token}` },
        body: JSON.stringify({
          chat_id: chatId, message_id: pendingTool.messageId,
          tool_name: pendingTool.name, args: pendingTool.args,
        }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || `HTTP ${res.status}`);
      setMessages(prev => prev.map(m => m.id === pendingTool.messageId
        ? { ...m, tool_result: { ...m.tool_result, status: j.status, result: j.result } } : m));
      // Side effect for navigate_to_page
      if (pendingTool.name === "navigate_to_page" && j.ok && j.result?.path) {
        navigate(j.result.path);
      }
      toast({
        title: j.ok ? t("supportChat.toolDone") : t("supportChat.toolFailed"),
        variant: j.ok ? "default" : "destructive",
      });
      setPendingTool(null);
      setLoading(true);
      try {
        const cont = await callChat({ continue_after_tool: true });
        handleResponse(cont);
      } finally { setLoading(false); }
    } catch (e: any) {
      toast({ title: t("supportChat.error"), description: e.message, variant: "destructive" });
    } finally { setExecuting(false); }
  };

  const cancelTool = () => {
    if (!pendingTool) return;
    setMessages(prev => prev.map(m => m.id === pendingTool.messageId
      ? { ...m, tool_result: { ...m.tool_result, status: "cancelled" } } : m));
    setPendingTool(null);
  };

  const suggestions = [
    t("supportChat.suggest1"),
    t("supportChat.suggest2"),
    t("supportChat.suggest3"),
    t("supportChat.suggest4"),
    t("supportChat.suggest5"),
    t("supportChat.suggest6"),
    t("supportChat.suggest7"),
    t("supportChat.suggest8"),
  ];

  const renderToolCard = (m: Msg) => {
    if (!m.tool_name) return null;
    const status = (m.tool_result?.status || "awaiting_confirmation") as ToolStatus;
    const isNav = m.tool_name === "navigate_to_page" && m.tool_result?.args?.path;

    // Inline CTA for navigation — no blocking popup.
    if (isNav && status !== "cancelled") {
      const path = m.tool_result.args.path as string;
      const reason = (m.tool_result.args.reason as string | undefined)?.trim();
      return (
        <div className="mt-2">
          <Button size="sm" variant="default" className="h-8 text-xs gap-1.5"
            onClick={() => navigate(path)}>
            <ExternalLink className="h-3.5 w-3.5" />
            <span>{reason || t("supportChat.openPage", "Open page")}</span>
            <span className="opacity-70 font-mono">{path}</span>
          </Button>
        </div>
      );
    }

    const color = status === "executed" ? "text-emerald-600" : status === "failed" ? "text-destructive" : status === "cancelled" ? "text-muted-foreground" : "text-amber-600";
    const Icon = status === "executed" ? CheckCircle2 : status === "failed" ? XCircle : Wrench;
    return (
      <div className="mt-2 rounded-md border bg-muted/40 p-2 text-xs">
        <div className={`flex items-center gap-1.5 font-medium ${color}`}>
          <Icon className="h-3.5 w-3.5" />
          <span>{t(WRITE_TOOL_LABELS[m.tool_name] || ("supportChat.tool." + m.tool_name) as any, m.tool_name)}</span>
          <span className="opacity-70">• {t(`supportChat.status.${status}` as any, status)}</span>
        </div>
      </div>
    );
  };

  const [confirmClear, setConfirmClear] = useState(false);
  const clearChat = async () => {
    if (chatId) {
      await supabase.from("support_chats").delete().eq("id", chatId);
    }
    setChatId(undefined);
    setMessages([]);
    setStreamingIdx(null);
    setPendingTool(null);
    setConfirmClear(false);
  };

  return (
    <div className="flex flex-col gap-3">
      {messages.length > 0 && (
        <div className="flex justify-end">
          <Button
            variant="ghost" size="sm"
            className="h-7 text-xs text-muted-foreground hover:text-destructive"
            onClick={() => setConfirmClear(true)}
          >
            <Trash2 className="h-3.5 w-3.5 me-1" />
            {t("supportChat.clear", "Clear chat")}
          </Button>
        </div>
      )}
      <div ref={scrollRef} className={`${height} overflow-y-auto rounded-md border bg-muted/20 p-3 space-y-3`}>
        {messages.length === 0 && !loading && (
          <div className="text-center text-muted-foreground text-sm py-8">
            <Bot className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="mb-1 font-medium text-foreground">{t("supportChat.welcome")}</p>
            <p className="mb-3 text-xs">{t("supportChat.welcomeSub")}</p>
            <div className="flex flex-wrap gap-2 justify-center">
              {suggestions.map((s, i) => (
                <Button key={i} type="button" variant="outline" size="sm" className="text-xs" onClick={() => setInput(s)}>{s}</Button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => {
          if (m.role === "tool") return null;
          return (
            <ChatBubble
              key={i}
              role={m.role as "user" | "assistant"}
              content={m.content}
              streaming={m.role === "assistant" && streamingIdx === i}
            >
              {m.role === "assistant" && renderToolCard(m)}
            </ChatBubble>
          );
        })}
        {loading && <TypingIndicator />}
      </div>
      <div className="flex gap-2 items-end">
        <Textarea
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder={t("supportChat.placeholder")}
          rows={compact ? 1 : 2} disabled={loading} dir="auto"
        />
        <Button onClick={send} disabled={loading || !input.trim()} size="icon">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>

      <AlertDialog open={!!pendingTool} onOpenChange={o => { if (!o && !executing) cancelTool(); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Wrench className="h-4 w-4 text-primary" />
              {pendingTool && t(WRITE_TOOL_LABELS[pendingTool.name] || "supportChat.tool.confirmTitle", pendingTool.name)}
            </AlertDialogTitle>
            <AlertDialogDescription>{t("supportChat.confirmDesc")}</AlertDialogDescription>
          </AlertDialogHeader>
          {pendingTool && (
            <pre className="text-xs bg-muted rounded p-3 overflow-auto max-h-64 whitespace-pre-wrap" dir="auto">
              {JSON.stringify(pendingTool.args, null, 2)}
            </pre>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={executing} onClick={cancelTool}>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction disabled={executing} onClick={confirmTool}>
              {executing && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {t("supportChat.run")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmClear} onOpenChange={setConfirmClear}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("supportChat.clearTitle", "Clear this chat?")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("supportChat.clearDesc", "All messages in this conversation will be permanently deleted. This cannot be undone.")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={clearChat} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {t("supportChat.clear", "Clear chat")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
